import { Brain, Check, X, AlertCircle, FileText, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';

const QuizViewer = ({ content, onComplete, isCompleted, previousResponse, gradingType = 'auto', taskStatus, lastSubmissionCancelled, onRequestRetake, allowRetakes, isAdmin = false, children }) => {

    const [quiz, setQuiz] = useState(() => {
        try {
            if (typeof content === 'object' && content !== null) return content;
            return JSON.parse(content);
        } catch (e) {
            console.error("Error parsing quiz content:", e);
            return { questions: [] };
        }
    });

    const [answers, setAnswers] = useState(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                return parsed.answers || {};
            } catch (e) { return {}; }
        }
        return {};
    });

    const [submitted, setSubmitted] = useState(isCompleted || taskStatus === 'completed' || taskStatus === 'retake_requested' || (isAdmin && previousResponse) || false);
    const [showResults, setShowResults] = useState((isCompleted || taskStatus === 'completed' || false) || (isAdmin && previousResponse));
    const isManualGrading = gradingType === 'manual';

    const [expandedQuestions, setExpandedQuestions] = useState(new Set());
    const toggleQuestion = (index) => {
        setExpandedQuestions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) newSet.delete(index);
            else newSet.add(index);
            return newSet;
        });
    };

    useEffect(() => {
        if (lastSubmissionCancelled) { setSubmitted(false); setShowResults(false); }
    }, [lastSubmissionCancelled]);

    useEffect(() => {
        if (taskStatus === 'completed' || isCompleted) { setSubmitted(true); setShowResults(true); }
        else if (taskStatus === 'retake_requested') { setSubmitted(true); setShowResults(false); }
        else if (taskStatus === 'viewed' || !taskStatus) { setSubmitted(false); setShowResults(false); }
    }, [taskStatus, isCompleted, allowRetakes, onRequestRetake]);

    useEffect(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                setAnswers(parsed.answers || {});
            } catch (e) { setAnswers({}); }
        } else { setAnswers({}); }
    }, [previousResponse]);

    const handleAnswerChange = (questionIndex, value) => {
        if (!submitted) setAnswers(prev => ({ ...prev, [questionIndex]: value }));
    };

    const calculateScore = () => {
        let correct = 0, totalPoints = 0, earnedPoints = 0;
        quiz.questions.forEach((question, index) => {
            const points = question.points || 1;
            totalPoints += points;
            if (answers[index] === question.correctAnswer) { correct++; earnedPoints += points; }
        });
        return { correct, total: quiz.questions.length, percentage: totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0, earnedPoints, totalPoints };
    };

    const results = React.useMemo(() => {
        if (submitted && !isManualGrading) return calculateScore();
        return null;
    }, [submitted, isManualGrading, answers, quiz]);

    const handleSubmit = () => {
        const answeredCount = Object.keys(answers).length;
        if (answeredCount < quiz.questions.length) {
            if (!window.confirm(`You have ${quiz.questions.length - answeredCount} unanswered questions. Submit anyway?`)) return;
        }
        const submissionResults = isManualGrading ? null : calculateScore();
        onComplete(JSON.stringify({ answers, results: submissionResults, gradingType, timestamp: new Date().toISOString() }), submissionResults ? submissionResults.percentage : null);
    };

    if (!quiz.questions || quiz.questions.length === 0) {
        return (
            <div className="text-center py-8 text-gray-400 dark:text-slate-500 text-sm">
                No quiz questions available
            </div>
        );
    }

    const answeredCount = Object.keys(answers).length;

    return (
        <div className="space-y-4">
            {/* Results Summary */}
            {submitted && results && (!isAdmin || (isAdmin && previousResponse)) && (
                <div className={`rounded-xl border p-5 ${results.percentage >= 70
                    ? 'border-green-200 dark:border-green-500/20 bg-green-50/50 dark:bg-green-500/5'
                    : 'border-red-200 dark:border-red-500/20 bg-red-50/50 dark:bg-red-500/5'
                }`}>
                    <div className="flex items-center justify-between mb-3">
                        <span className={`text-sm font-semibold ${results.percentage >= 70 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                            {results.percentage >= 70 ? 'Passed' : 'Failed'}
                        </span>
                        <span className={`text-2xl font-bold ${results.percentage >= 70 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                            {results.percentage}%
                        </span>
                    </div>
                    <div className="flex gap-6 text-sm text-gray-500 dark:text-slate-400">
                        <span>{results.correct}/{results.total} correct</span>
                        <span>{results.earnedPoints}/{results.totalPoints} points</span>
                    </div>
                </div>
            )}

            {/* Progress indicator when taking quiz */}
            {!submitted && (
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-slate-400">
                        {answeredCount} of {quiz.questions.length} answered
                    </span>
                    <span className="text-gray-500 dark:text-slate-400 font-medium">
                        {quiz.questions.reduce((sum, q) => sum + (q.points || 1), 0)} points
                    </span>
                </div>
            )}

            {/* Questions */}
            <div className="space-y-3">
                {quiz.questions.map((question, qIndex) => {
                    const hasAnswer = Object.prototype.hasOwnProperty.call(answers, qIndex);
                    const userAnswerIndex = hasAnswer ? answers[qIndex] : undefined;
                    const isCorrect = !isManualGrading && userAnswerIndex === question.correctAnswer;
                    const isExpanded = !isAdmin || expandedQuestions.has(qIndex);

                    let borderClass = 'border-gray-200 dark:border-slate-700/60';
                    if (submitted && !isManualGrading && !isAdmin) {
                        if (isCorrect) borderClass = 'border-green-200 dark:border-green-500/20 bg-green-50/30 dark:bg-green-500/5';
                        else if (hasAnswer) borderClass = 'border-red-200 dark:border-red-500/20 bg-red-50/30 dark:bg-red-500/5';
                    }

                    return (
                        <div key={qIndex} className={`rounded-xl border ${borderClass} bg-white dark:bg-slate-900 overflow-hidden`}>
                            {/* Question Header */}
                            <div
                                className={`px-5 py-4 flex items-start gap-3 ${isAdmin ? 'cursor-pointer' : ''}`}
                                onClick={() => isAdmin && toggleQuestion(qIndex)}
                            >
                                {isAdmin && (
                                    <div className="mt-1 text-gray-400 dark:text-slate-500">
                                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </div>
                                )}
                                <span className="text-sm font-medium text-gray-400 dark:text-slate-500 mt-0.5 select-none">{qIndex + 1}.</span>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 dark:text-white text-[15px] leading-relaxed">
                                        {question.question}
                                    </p>
                                    {submitted && !isManualGrading && !isAdmin && (
                                        <span className={`inline-flex items-center gap-1 text-xs font-medium mt-1.5 ${isCorrect ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {isCorrect ? <><Check className="w-3 h-3" /> Correct</> : <><X className="w-3 h-3" /> Incorrect</>}
                                        </span>
                                    )}
                                </div>
                                <span className="text-xs text-gray-400 dark:text-slate-500 whitespace-nowrap mt-1">
                                    {question.points || 1} pt{(question.points || 1) !== 1 ? 's' : ''}
                                </span>
                            </div>

                            {/* Options */}
                            {isExpanded && (
                                <div className="px-5 pb-4 space-y-2">
                                    {(isManualGrading || !question.options || question.options.length === 0) ? (
                                        <textarea
                                            value={userAnswerIndex || ''}
                                            onChange={(e) => handleAnswerChange(qIndex, e.target.value)}
                                            disabled={submitted || isAdmin}
                                            placeholder="Type your answer here..."
                                            rows="3"
                                            className="w-full px-4 py-3 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:text-gray-500 dark:disabled:text-slate-400 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                        />
                                    ) : (
                                        question.options.map((option, oIndex) => {
                                            const isSelected = userAnswerIndex === oIndex;
                                            const isCorrectOption = oIndex === question.correctAnswer;

                                            let optClass = 'border rounded-lg px-4 py-3 flex items-center gap-3 transition-all text-sm ';
                                            if (!submitted && !isAdmin) {
                                                optClass += 'cursor-pointer ';
                                                optClass += isSelected
                                                    ? 'border-blue-300 dark:border-blue-500/40 bg-blue-50/50 dark:bg-blue-500/10'
                                                    : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600';
                                            } else {
                                                if (!isManualGrading) {
                                                    if (isCorrectOption && (submitted || isAdmin))
                                                        optClass += 'border-green-300 dark:border-green-500/30 bg-green-50/50 dark:bg-green-500/5';
                                                    else if (isSelected && !isCorrectOption)
                                                        optClass += 'border-red-300 dark:border-red-500/30 bg-red-50/50 dark:bg-red-500/5';
                                                    else
                                                        optClass += 'border-gray-100 dark:border-slate-700/40 opacity-50';
                                                } else {
                                                    optClass += isSelected
                                                        ? 'border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800'
                                                        : 'border-gray-100 dark:border-slate-700/40 opacity-50';
                                                }
                                            }

                                            return (
                                                <label key={oIndex} className={optClass}>
                                                    <input
                                                        type="radio"
                                                        name={`question-${qIndex}`}
                                                        checked={isSelected}
                                                        onChange={() => handleAnswerChange(qIndex, oIndex)}
                                                        disabled={submitted || isAdmin}
                                                        className="w-4 h-4 text-blue-600 dark:text-blue-500 border-gray-300 dark:border-slate-500 focus:ring-blue-500"
                                                    />
                                                    <span className={`flex-1 ${isSelected ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-700 dark:text-slate-300'}`}>
                                                        {option}
                                                    </span>
                                                    {(submitted || isAdmin) && !isManualGrading && isCorrectOption && (
                                                        <Check className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                                    )}
                                                    {(submitted || isAdmin) && !isManualGrading && isSelected && !isCorrectOption && (
                                                        <X className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                                                    )}
                                                </label>
                                            );
                                        })
                                    )}

                                    {/* Explanation */}
                                    {(submitted || isAdmin) && !isManualGrading && question.explanation && (
                                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 rounded-lg">
                                            <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Explanation</p>
                                            <p className="text-sm text-blue-600 dark:text-blue-400 leading-relaxed">{question.explanation}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Children (Proof/Signature) */}
            {!submitted && !isAdmin && children}

            {/* Submit */}
            {!isAdmin && (
                <div className="flex justify-end pt-2">
                    {!submitted ? (
                        <button
                            onClick={handleSubmit}
                            className="px-5 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center gap-2"
                        >
                            <Check className="w-4 h-4" />
                            {isManualGrading ? 'Submit for Review' : 'Submit Quiz'}
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            {allowRetakes && onRequestRetake && (
                                <button
                                    onClick={onRequestRetake}
                                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-slate-700 rounded-lg transition-colors"
                                >
                                    Request Retake
                                </button>
                            )}
                            <span className="inline-flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 font-medium px-4 py-2">
                                <Check className="w-4 h-4" />
                                {isManualGrading ? 'Submitted for Review' : 'Completed'}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuizViewer;
